import { useState, useEffect, useCallback } from 'react';
import { Card, Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getInitialsFromName, resolveAvatarSrc } from 'utils/avatar';
import { toast } from 'react-toastify';
import { getSocket } from '../../../../../socket';

export default function PollContent({ pollId, getPolls, onPollClosed, isPollManager }) {
  const [poll, setPoll] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasVoted, setHasVoted] = useState(false);
  const [optionAvatars, setOptionAvatars] = useState({});
  const [selectedOptionId, setSelectedOptionId] = useState(null);
  const [voteSubmitting, setVoteSubmitting] = useState(false);

  const url = process.env.REACT_APP_URL;
  const params = useParams();
  const meetingId = params.meetingId;
  const { user: authUser } = useSelector((x) => x.auth);

  // console.log('PollId', pollId);

  // Fetch poll data (stable via useCallback)
  const fetchPoll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${url}/poll/${pollId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(authUser?.token ? { Authorization: `Bearer ${authUser.token}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch poll: ${response.statusText}`);
      }

      const data = await response.json();
      setPoll(data);
      if (authUser?.token) {
        setSelectedOptionId(data.myVoteOptionId || null);
        setHasVoted(data.status === 'active' && Boolean(data.myVoteOptionId));
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [url, pollId, authUser?.token]);

  // Fetch poll on mount / when fetchPoll changes
  useEffect(() => {
    fetchPoll();
  }, [fetchPoll]);

  // Determine whether the browser has already voted for this poll; update when poll or pollId changes
  useEffect(() => {
    if (authUser?.token) return;
    const votedPolls = JSON.parse(localStorage.getItem('votedPolls') || '[]');
    let voted = false;
    if (votedPolls.length > 0) {
      if (typeof votedPolls[0] === 'string') {
        voted = votedPolls.includes(pollId);
      } else if (typeof votedPolls[0] === 'object') {
        voted = votedPolls.some(p => p.id === pollId && Date.now() < Number(p.expiry || 0));
      }
    }
    if (voted && poll && poll.status === 'active') {
      setHasVoted(true);
    } else {
      setHasVoted(false);
    }
  }, [authUser?.token, poll, pollId]);

  useEffect(() => {
    let mounted = true;
    if (!poll || !Array.isArray(poll.options)) return () => { mounted = false };
    poll.options.forEach((option) => {
      const key = String(option?._id || option?.tmAuthUserId || option?.userId || '').trim();
      if (!key) return;
      const existing = resolveAvatarSrc(option.avatarUrl || option.avatar);
      if (existing) return;
      if (optionAvatars[key]) return;

      (async () => {
        try {
          const resp = await fetch(`${url}/unified/${encodeURIComponent(key)}`);
          if (!mounted) return;
          if (!resp.ok) return;
          const payload = await resp.json().catch(() => null);
          const avatar = payload?.identity?.avatarUrl || payload?.identity?.avatar || null;
          const src = avatar ? resolveAvatarSrc(avatar) : null;
          if (src && mounted) setOptionAvatars(prev => ({ ...prev, [key]: src }));
        } catch (_err) {
          // ignore
        }
      })();
    });

    return () => { mounted = false };
  }, [poll, optionAvatars, url]);

  const handleVote = async () => {
    if (!selectedOptionId) {
      toast.error('Please select an option before voting.', { position: 'bottom-left' });
      return;
    }
    setVoteSubmitting(true);
    try {
      // Fetch the latest poll data to verify the status
      const response = await fetch(`${url}/poll/${pollId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(authUser?.token ? { Authorization: `Bearer ${authUser.token}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch poll status: ${response.statusText}`);
      }

      const latestPoll = await response.json();

      // Check if the poll status is still 'active'
      if (latestPoll.status !== 'active') {
        toast.warning('The vote cannot be cast. Vote session is not active. Please reload the poll.', { position: 'bottom-left' });
        setVoteSubmitting(false);
        return;
      }

      // Proceed with submitting the vote
      const now = Date.now();
      const validPolls = authUser?.token
        ? []
        : JSON.parse(localStorage.getItem('votedPolls') || '[]').filter((entry) => (
          typeof entry === 'string' || now < Number(entry?.expiry || 0)
        ));
      if (!authUser?.token) localStorage.setItem('votedPolls', JSON.stringify(validPolls));

      // Check if the current poll is already voted
      if (authUser?.token || !validPolls.some((entry) => (typeof entry === 'string' ? entry : entry.id) === pollId)) {
        const voteResponse = await fetch(`${url}/vote/${pollId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...(authUser?.token ? { Authorization: `Bearer ${authUser.token}` } : {}),
            // 'Authorization': `Bearer ${authUser.token}`,
          },
          body: JSON.stringify({ optionId: selectedOptionId }),
        });

        if (!voteResponse.ok) {
          setVoteSubmitting(false);
          throw new Error(`Failed to submit vote: ${voteResponse.statusText}`);
        }

        if (!authUser?.token) {
          validPolls.push({ id: pollId, expiry: now + 10 * 60 * 1000 });
          localStorage.setItem('votedPolls', JSON.stringify(validPolls));
        }
        setHasVoted(true);

        await fetchPoll();
      } else {
        toast.info('You have already voted for this poll.', { position: 'bottom-left' });
      }
      setVoteSubmitting(false);
    } catch (error) {
      setVoteSubmitting(false);
      toast.error(`Failed to submit vote: ${error.message}`, { position: 'bottom-left' });
    }
  };

  const handleSetStatus = async (nextStatus) => {
    if (!isPollManager || !poll || !nextStatus) return;

    try {
      const response = await fetch(`${url}/poll/${pollId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authUser.token}`,
        },
        body: JSON.stringify({ status: nextStatus }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update poll status: ${response.statusText}`);
      }

      await response.json();
      await fetchPoll();
      getPolls?.(); // Refresh the list of polls
    } catch (error) {
      console.error(`An error occurred while updating the poll status: ${error.message}`);
      toast.error(`Failed to update poll status: ${error.message}`, { position: 'bottom-left' });
    }
  };

  useEffect(() => {
    const effectiveMeetingId = String(poll?.meeting || meetingId || '').trim();
    if (!pollId) return undefined;

    const socket = getSocket();
    if (effectiveMeetingId) {
      socket.emit('meeting:join', effectiveMeetingId);
    }

    const handlePollStatusChanged = (payload) => {
      if (!payload || String(payload.pollId) !== String(pollId)) return;

      if (payload.status === 'deleted') {
        getPolls?.();
        return;
      }

      setPoll((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          status: payload.status,
        };
      });

      // Refetch full poll data to get updated options, votes, and winners
      void fetchPoll();
    };

    socket.on('poll:status-changed', handlePollStatusChanged);

    return () => {
      socket.off('poll:status-changed', handlePollStatusChanged);
    };
  }, [fetchPoll, getPolls, meetingId, poll?.meeting, pollId]);

  if (isLoading) {
    return <p className="poll-state-message">Loading poll...</p>;
  }

  if (error) {
    return <p className="poll-state-message text-danger">Error: {error}</p>;
  }

  if (!poll) {
    return <p className="poll-state-message">No poll data available.</p>;
  }

  // Always render the poll card, but only allow selection when poll.status === 'active'
  let pollContent = null;
  if (hasVoted && poll.status === 'active' && !authUser?.token) {
    pollContent = (
      <p className="poll-state-message">
        Thank you for your vote! The results will be announced later.
      </p>
    );
  } else if (!hasVoted && (poll.status === 'ready' || poll.status === 'active' || poll.status === 'closed')) {
    pollContent = (
      <>
        <div className="poll-options-grid">
          {poll.options.map((option, index) => {
              const isSelected = poll.status === 'active' && selectedOptionId === option._id;
              const optionInitials = getInitialsFromName(option?.firstName, option?.lastName, 'T');
              const key = String(option?._id || option?.tmAuthUserId || option?.userId || '').trim();
              const src = resolveAvatarSrc(option.avatarUrl || option.avatar) || (key ? optionAvatars[key] : null);

              return (
                <button
                  type="button"
                  key={index}
                  className={`poll-option-card${isSelected ? ' is-selected' : ''}`}
                  disabled={poll.status !== 'active'}
                  onClick={() => poll.status === 'active' && setSelectedOptionId(option._id)}
                  aria-pressed={isSelected}
                >
                  {src ? (
                    <img className="poll-option-avatar" src={src} alt="" />
                  ) : (
                    <span className="poll-option-avatar poll-option-avatar--fallback">{optionInitials}</span>
                  )}
                  <span className="poll-option-copy">
                    <span className="poll-option-name">{option.firstName} {option.lastName}</span>
                    <span className="poll-option-title">{option.title || poll.title}</span>
                  </span>
                  {isSelected && <i className="bi bi-check-circle-fill poll-option-selected-icon" aria-hidden="true" />}
                </button>
              );
          })}
        </div>
        {poll.status === 'active' &&
          <div className="poll-submit-row">
            <Button variant="primary" onClick={handleVote} disabled={voteSubmitting}>
              {voteSubmitting ? 'Submitting...' : hasVoted ? 'Update Vote' : 'Submit Vote'}
            </Button>
          </div>
        }
      </>
    );
  } else if (!hasVoted && !(poll.status === 'ready' || poll.status === 'active' || poll.status === 'closed')) {
    pollContent = (
      (() => {
        const maxVotes = Math.max(0, ...poll.options.map((option) => Number(option.votes) || 0));
        const winners = poll.options.filter((option) => Number(option.votes) === maxVotes && maxVotes > 0);
        return (
          <section className="poll-winners">
            <h1>{winners.length === 1 ? 'Winner' : 'Winners'}</h1>
            {winners.length ? (
              <div className="poll-winners-grid">
                {winners.map((option, index) => {
                  const winnerInitials = getInitialsFromName(option?.firstName, option?.lastName, 'T');
                  const key = String(option?._id || option?.tmAuthUserId || option?.userId || '').trim();
                  const src = resolveAvatarSrc(option.avatarUrl || option.avatar) || (key ? optionAvatars[key] : null);

                  return (
                    <article className="poll-winner-card" key={option?._id || key || index}>
                      {src ? (
                        <img className="poll-winner-avatar" src={src} alt="" />
                      ) : (
                        <span className="poll-winner-avatar poll-winner-avatar--fallback">{winnerInitials}</span>
                      )}
                      <div className="poll-winner-copy">
                        <strong>{option.firstName} {option.lastName}</strong>
                        <span>{option.title || poll.title}</span>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <p className="poll-state-message">There are no votes to display yet.</p>
            )}
          </section>
        );
      })()
    );
  }

  return (
    <Card className="poll-static-card">
      <Card.Header>
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <span className="fw-semibold">{poll.title}</span>
          {isPollManager ? (
            <div className="d-flex gap-1 flex-wrap">
              {['ready', 'active', 'closed', 'displayed'].map((statusKey) => {
                const isCurrent = poll.status === statusKey;
                const variantMap = {
                  ready: isCurrent ? 'success' : 'outline-success',
                  active: isCurrent ? 'warning' : 'outline-warning',
                  closed: isCurrent ? 'danger' : 'outline-danger',
                  displayed: isCurrent ? 'info' : 'outline-info',
                };
                return (
                  <Button
                    key={statusKey}
                    size="sm"
                    variant={variantMap[statusKey]}
                    onClick={() => handleSetStatus(statusKey)}
                    className="text-capitalize py-0 px-2"
                    style={{
                      fontSize: '0.8rem',
                      fontWeight: isCurrent ? 700 : 500,
                      boxShadow: isCurrent ? '0 0 6px rgba(255,255,255,0.3)' : 'none',
                    }}
                  >
                    {statusKey}
                  </Button>
                );
              })}
            </div>
          ) : (
            poll.status === 'ready' ? (
              <span className="badge bg-success">{poll.status}</span>
            ) : poll.status === 'active' ? (
              <span className="badge bg-warning">{poll.status}</span>
            ) : poll.status === 'closed' ? (
              <span className="badge bg-danger">{poll.status}</span>
            ) : poll.status === 'displayed' ? (
              <span className="badge bg-info">{poll.status}</span>
            ) : null
          )}
        </div>
      </Card.Header>
      <Card.Body className="poll-static-card-body">
        {isPollManager && (
          <div className="poll-reload-row">
            <Button
              variant="primary"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                fetchPoll();
                toast.info('Poll reloaded successfully', { position: 'bottom-left' });
              }}
            >
              Reload <i className="bi bi-arrow-clockwise" />
            </Button>
          </div>
        )}
        {pollContent}
      </Card.Body>
    </Card>
  );
}
